// services/ESignService.js
import axios from "axios";
import authUtil from "../utils/AuthUtils";
import { getServiceApiKey } from "../utils/apiKey";

const BASE_API_URL = `${window?.appConfig?.API_ENDPOINT || 'https://qa-document-service-api.cargoes.com'}`;
// Helper function to create config
const getConfig = async () => {
  const token = await authUtil.getDocumentServiceToken();
  return {
    headers: {
      "x-api-key": getServiceApiKey(),
      "x-document-service-integration-token": token,
    },
  };
};

/**
 * Get document details for signing using the document viewing token
 * @param {string} token - Document viewing token
 * @returns {Promise} - Promise with document data
 */
async function getDocumentForSigning(token) {
  return axios.get(BASE_API_URL + `/e-sign/sign/${token}`);
}

/**
 * Sign a document with signatures
 * @param {string} documentId - Document ID
 * @param {string} token - Document viewing token
 * @param {Object} signatureData - Object containing signature data
 * @returns {Promise} - Promise with signing result
 */
async function signDocument(documentId, token, signatureData) {
  return axios.post(
    BASE_API_URL + `/e-sign/sign/${documentId}?token=${token}`,
    signatureData
  );
}


// change here to get the documents with desired status
// Updated ESign Service function
async function getDocuments(params = {}) {
  try {
    const config = await getConfig();
    let allDocuments = [];
    let currentPage = params._page || 1;
    let targetLimit = params._limit || 10;
    let hasMorePages = true;
    let totalFetched = 0;
    let totalCount = 0;
    
    // Keep fetching until we have enough non-draft documents or no more pages
    while (allDocuments.length < targetLimit && hasMorePages) {
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      queryParams.append('_page', currentPage);
      queryParams.append('_limit', targetLimit * 2); // Fetch more to account for filtering
      if (params._sort) queryParams.append('_sort', params._sort);
      if (params._order) queryParams.append('_order', params._order);
      
      // Add filter parameters
      if (params.status) queryParams.append('status', params.status);
      if (params.q) queryParams.append('q', params.q);
      if (params.createdDateFrom) queryParams.append('createdDateFrom', params.createdDateFrom);
      if (params.createdDateTo) queryParams.append('createdDateTo', params.createdDateTo);
      
      const response = await axios.get(BASE_API_URL + `/e-sign/documents?${queryParams.toString()}`, {
        headers: {
          ...config.headers,
        },
        withCredentials: true,
      });
      
      if (response.data && response.data.documents) {
        // Filter out documents with status 'draft'
        const filteredDocuments = response.data.documents.filter(doc => doc.status !== 'draft');
        allDocuments = allDocuments.concat(filteredDocuments);
        
        totalCount = response.data.pagination?.totalCount || 0;
        hasMorePages = response.data.pagination?.currentPage < response.data.pagination?.totalPages;
        currentPage++;
        
        // If no more documents in this batch, stop
        if (response.data.documents.length === 0) {
          hasMorePages = false;
        }
      } else {
        hasMorePages = false;
      }
    }
    
    // Take only the requested number of documents
    const documentsForPage = allDocuments.slice(0, targetLimit);
    
    // Create response structure
    const result = {
      data: {
        success: true,
        documents: documentsForPage,
        pagination: {
          currentPage: params._page || 1,
          totalCount: totalCount, // This is approximate since we don't know total draft count
          totalPages: Math.ceil(totalCount / targetLimit),
          pageSize: targetLimit
        }
      }
    };
    
    return result;
  } catch (error) {
    console.error('Error fetching documents:', error);
    return null;
  }
}

async function getDocument(id) {
  try {
    const response = await axios.get(
      `${BASE_API_URL}/e-sign/documents/${id}/signing-status`,
      {
        headers: {
          "x-api-key": getServiceApiKey(),
        },
        withCredentials: true, // include if the cookie is needed
      }
    );

    return response;
  } catch (error) {
    console.error(`Error fetching signing status for document ${id}:`, error);
    return null;
  }
}

async function uploadDocument(formData) {
  try {
    const config = await getConfig(); // fetch headers

    const response = await axios.post(
      BASE_API_URL + '/e-sign/documents/upload',
      formData,
      {
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true, // needed for cookie
      }
    );

    return response;
  } catch (error) {
    console.error('Error uploading document:', error);
    return null;
  }
}

/**
 * Download signed document
 * @param {string} documentId - Document ID
 * @param {string} token - Document viewing token
 * @returns {Promise} - Promise with document blob
 */
async function downloadSignedDocument(documentId, token) {
  return axios.get(
    BASE_API_URL + `/e-sign/documents/${documentId}/download?token=${token}`,
    {
      responseType: 'blob'
    }
  );
}

/**
 * Reject a document with a comment
 * @param {string} documentId - Document ID
 * @param {string} token - Document viewing token
 * @param {Object} rejectionData - Object containing rejection comment
 * @returns {Promise} - Promise with rejection result
 */
async function rejectDocument(documentId, token, rejectionData) {
  return axios.post(
    BASE_API_URL + `/e-sign/reject/${documentId}?token=${token}`,
    rejectionData,
    {
      headers: {
            "x-api-key": getServiceApiKey(),
      },
      withCredentials: true, // include if the cookie is needed
    }
  );
}

async function addSignatureFields(documentId, fieldData) {
  try {
    const config = await getConfig(); // headers with tokens and api key

    const response = await axios.post(
      `${BASE_API_URL}/e-sign/documents/${documentId}/fields`,
      fieldData,
      {
        headers: {
          ...config.headers,
          'Content-Type': 'application/json',
        },
        withCredentials: true, // include if the cookie is needed
      }
    );

    return response;
  } catch (error) {
    console.error(`Error adding signature fields for document ${documentId}:`, error);
    return null;
  }
}

async function addRecipients(documentId, recipientData) {
  try {
    const config = await getConfig(); // includes headers with tokens and API key

    const response = await axios.post(
      `${BASE_API_URL}/e-sign/documents/${documentId}/recipients`,
      recipientData,
      {
        headers: {
          ...config.headers,
          'Content-Type': 'application/json',
        },
      }
    );

    return response;
  } catch (error) {
    console.error(`Error adding recipients for document ${documentId}:`, error?.response?.data || error.message);
    return null;
  }
}

async function sendDocument(documentId, payload) {
  try {
    const config = await getConfig(); // Retrieves headers with API key and integration token

    const response = await axios.post(
      `${BASE_API_URL}/e-sign/documents/${documentId}/send`,
      payload,
      {
        headers: {
          ...config.headers,
          'Content-Type': 'application/json',
        },
      }
    );

    return response;
  } catch (error) {
    console.error(`Error sending document ${documentId}:`, error?.response?.data || error.message);
    return null;
  }
}

// API call to get document from storage
async function getDocumentFromStorage(fileIdentifier) {
  try {
    const config = await getConfig(); // Await headers config

    const response = await axios.get(
      `${BASE_API_URL}/file-storage/file`,
      {
        params: { fileIdentifier },
        headers: {
          ...config.headers,
          "X-DPW-ApplicationId": "datachain",
        },
        withCredentials: true,
        responseType: 'blob',
      }
    );

    return response;
  } catch (error) {
    console.error(`Error fetching document from storage for ID ${fileIdentifier}:`, error);
    return null;
  }
}

async function getSignedDocumentFromStorage(fileIdentifier) {
  try {
    const config = await getConfig(); // Await headers config

    const response = await axios.get(
      `${BASE_API_URL}/e-sign/final-signed/${fileIdentifier}`,
      {
        headers: {
          ...config.headers,
          "X-DPW-ApplicationId": "datachain",
        },
        withCredentials: true,
        responseType: 'blob',
      }
    );

    return response;
  } catch (error) {
    console.error(`Error fetching signed document from storage for ID ${fileIdentifier}:`, error);
    return null;
  }
}


export default {
  getDocumentForSigning,
  signDocument,
  downloadSignedDocument,
  rejectDocument,
  getDocuments,
  uploadDocument,
  getDocument,
  addSignatureFields,
  addRecipients,
  sendDocument,
  getDocumentFromStorage,
  getSignedDocumentFromStorage
};